/**
 * M2.4 series cover → playlist images. The series' cover options (square images, made by the Studio or uploaded) and the
 * chosen one: "Choose" saves at once (PUT /api/series/:id/cover) and the worker sets it as the image of the series playlist on
 * every channel — at creation, and again after every new choice. "Upload" = POST /api/uploads → POST /api/series/:id/covers.
 */
import { useId, useRef, useState } from "react";
import { COVER_CONTENT_TYPES, COVER_MAX_BYTES } from "@contentops/contract";
import type { CoverView, SeriesCovers, SeriesPlaylist } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, ErrorNote, Loading, Progress } from "../../components/ui";
import { useApi, useCache, useMutation } from "../../data/hooks";
import { seriesCoversKey, useSeriesCovers } from "../../data/queries";
import type { Tone } from "../../lib/status";

const coverName = (c: CoverView, i: number) => c.label || `Series cover ${i + 1}`;
const ACCEPT = COVER_CONTENT_TYPES.join(",");

/** The playlist image line of one playlist row, in plain words. */
export function playlistImageText(img: SeriesPlaylist["image"]): { text: string; tone: Tone; detail: string | null } | null {
  if (!img || img.state === "none") return null;
  switch (img.state) {
    case "set": return { text: "Playlist image set", tone: "ok", detail: img.message };
    case "pending": return { text: "Playlist image waiting to be set", tone: "info", detail: img.message };
    case "not_eligible": return { text: "Playlist image not allowed yet", tone: "attention", detail: img.message };
    case "failed": return { text: "Playlist image not set", tone: "danger", detail: img.message };
  }
}

export function SeriesCoverPicker({ seriesId, invalidate = [] }: { seriesId: string; invalidate?: string[] }) {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const q = useSeriesCovers(seriesId);
  const data = q.data;
  const fileRef = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<number | null>(null);
  const [choosing, setChoosing] = useState<string | null>(null);
  const hid = useId();
  const store = (r: SeriesCovers) => cache.setData(seriesCoversKey(seriesId), r);

  const choose = useMutation((artifactId: string) => api.chooseSeriesCover(seriesId, artifactId), { invalidate, onSuccess: store });
  const onChoose = async (c: CoverView, i: number) => {
    setChoosing(c.artifact_id);
    try {
      await choose.mutateAsync(c.artifact_id);
      toast.ok(`“${coverName(c, i)}” is the series cover now — it becomes the image of the series playlist on every channel.`);
    } catch (e) {
      toast.error(e, "Couldn't choose that series cover");
    } finally {
      setChoosing(null);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!(COVER_CONTENT_TYPES as readonly string[]).includes(file.type)) { toast.show({ tone: "danger", text: "A series cover must be a square PNG or JPEG image." }); return; }
    if (file.size > COVER_MAX_BYTES) { toast.show({ tone: "danger", text: `That image is ${Math.ceil(file.size / 1024 / 1024)} MB — YouTube takes playlist images up to ${COVER_MAX_BYTES / 1024 / 1024} MB.` }); return; }
    setUpload(0);
    try {
      const { artifact_id } = await api.upload(file, { kind: "other", onProgress: setUpload });
      store(await api.attachSeriesCover(seriesId, artifact_id));
      toast.ok("Series cover added. Tap Choose to use it.");
    } catch (e) {
      toast.error(e, "Couldn't add the series cover");
    } finally {
      setUpload(null);
    }
  };

  const chosen = data?.covers.find((c) => c.artifact_id === data.chosen_artifact_id);
  return (
    <details className="series-cover">
      <summary>
        <Icon name="chevronDown" size={18} /> Series cover
        {chosen ? <Badge tone="ok">Chosen</Badge> : data ? <Badge tone="neutral">None chosen</Badge> : null}
      </summary>
      <div className="series-cover-body">
        <p className="muted small">A square PNG or JPEG (1:1, e.g. 1024×1024). The chosen one becomes the image of this series' playlist on every channel — when the playlist is created, and again whenever you choose another.</p>
        {upload !== null && <Progress value={upload} label="Uploading series cover" />}
        {!data ? (q.error ? <ErrorNote error={q.error} onRetry={() => q.refetch()} /> : <Loading label="Loading series covers…" />)
          : !data.covers.length ? <p className="muted small">No series cover yet. The Studio can make one, or upload a square image.</p>
          : (
            <ul className="covers is-square" aria-label="Series covers">
              {data.covers.map((c, i) => {
                const isChosen = data.chosen_artifact_id === c.artifact_id;
                return (
                  <li key={c.artifact_id} className="cover-item" aria-label={coverName(c, i)}>
                    <div className={`cover${isChosen ? " is-chosen" : ""}`}>
                      <img src={api.artifactUrl(c.artifact_id)} alt={coverName(c, i)} loading="lazy" />
                      {isChosen && <span className="cover-check"><Icon name="check" size={18} /></span>}
                    </div>
                    {isChosen ? <Badge tone="ok">Chosen</Badge> : (
                      <Button size="sm" variant="secondary" disabled={!c.settable || (choose.pending && choosing !== c.artifact_id)} busy={choosing === c.artifact_id}
                        aria-describedby={c.problem ? `${hid}-${i}` : undefined} onClick={() => void onChoose(c, i)}>Choose</Button>
                    )}
                    {c.problem && <p id={`${hid}-${i}`} className="cover-problem">{c.problem}</p>}
                  </li>
                );
              })}
            </ul>
          )}
        <div className="series-cover-actions">
          <Button size="sm" variant="ghost" icon="upload" busy={upload !== null} onClick={() => fileRef.current?.click()}>Upload series cover</Button>
          <input ref={fileRef} type="file" accept={ACCEPT} hidden aria-label="Series cover image file" onChange={(e) => void onFile(e.target.files?.[0])} />
        </div>
      </div>
    </details>
  );
}
